import { metaDataKey } from '@common/prosemirror/plugins/meta-data';
import { EditorEvent } from '@portal-core/project-files/events/editor-event.event';
import { NodeViewBase } from '@portal-core/text-editor/node-views/node-view.base';
import { GetPosForNodeView } from '@portal-core/text-editor/node-views/types/nodeview-get-pos.type';
import { BidiBridge } from '@portal-core/util/bidi-bridge';
import { Node } from 'prosemirror-model';
import { Decoration, EditorView, NodeView } from 'prosemirror-view';
import { Subscription } from 'rxjs';

export class VariableNodeView extends NodeViewBase implements NodeView {
  private metaDataUpdateSubscription: Subscription;
  private value: string | undefined;

  constructor(
    node: Node,
    editorView: EditorView,
    getPos: GetPosForNodeView,
    decorations: Decoration[],
    nodeViewBridge: BidiBridge<EditorEvent, EditorEvent>
  ) {
    super(node, editorView, getPos, decorations, nodeViewBridge);

    // Listen to the meta data plugin for updates so that the variable value can be updated
    const metaDataPlugin = metaDataKey.get(this.editorView.state);

    this.metaDataUpdateSubscription = metaDataPlugin.spec.update$.subscribe(() => {
      this.syncNode(this.node);
    });

    // Sync the node
    this.syncNode(node);
  }

  destroy() {
    this.metaDataUpdateSubscription?.unsubscribe();
    super.destroy();
  }

  syncNode(node: Node) {
    const definition = this.convertToValue(node.attrs.name);
    const value = this.processVariableText(definition, true);

    // Only emit loaded if the value has changed
    const emitLoaded = value !== this.value;

    this.value = value;

    if (value === undefined) {
      this.dom.textContent = 'Loading...';
    } else {
      this.dom.textContent = value ?? '';
    }

    if (emitLoaded) {
      this.emitEvent('node-view-size-changed', { nodeName: node.type.name });
    }
  }

  private convertToValue(longName: string) {
    if (this.isSystem(longName)) {
      return this.formatSystemVariable(longName);
    }

    const metaDataState = metaDataKey.getState(this.editorView.state);
    const variablesMeta = metaDataState?.metaData?.variables;
    const variableMetaLoaded = !!variablesMeta;
    const variable = variablesMeta ? variablesMeta[longName] : null;

    if (variable) {
      return variable['definition'];
    } else if (variableMetaLoaded) {
      return this.formatUndefinedVariable(longName);
    }
  }

  private formatSystemVariable(longName: string) {
    return `Variable: ${longName}`;
  }

  private formatUndefinedVariable(longName: string) {
    return longName ? `Undefined variable: ${longName}` : 'Undefined variable';
  }

  private isSystem(longName: string): boolean {
    if (!longName)
      return false;
    return longName.startsWith('System.') || longName.startsWith('Heading.');
  }

  private formatTextVariable(longName: string): string {
    return `[%=${longName}%]`;
  }

  /**
   * This code is the same as the Flare VariableTextParser.ProcessVariableText method cause it is used for output files.
   * It's not resolve broken formatted variables that has three or more '[%=' in a row
   * One difference is the added level of nesting to avoid the recursive problem.
   */
  private processVariableText(text: string, removeNewLinesAndTabs: boolean, nestingLevel: number = 0): string {
    if (!text)
      return text;

    let loop: string = text;

    while (true) {
      let start: number = loop.indexOf("[%=");
      if (start === -1) return text;

      let end: number = loop.indexOf("%]");
      if (end === -1) return text;

      //skip over broken formatted variables
      let nextStart: number = loop.indexOf("[%=", start + 3);
      if (nextStart !== -1 && nextStart < end) {
        let nextEnd: number = loop.indexOf("%]");
        if (nextEnd === -1) return text;
      }
      else {
        nextStart = start;
      }

      if (end > nextStart) {
        let temp: string = loop.substring(nextStart + 3, end);
        if (temp) {
          let newtext: string = this.convertToValue(temp);

          if (removeNewLinesAndTabs) {
            newtext = newtext.replace(/\t|\n|\r/g, " ");
          }

          if (newtext !== temp) {
            let fulltemp: string = this.formatTextVariable(temp);

            if (nestingLevel == 0) {
              try {
                text = this.processVariableText(text.replace(fulltemp, newtext), false, nestingLevel + 1);
              } catch (error) {
                return text.replace(fulltemp, `Variable with recursive problem: ${temp}`);
              }
            }
            if (nestingLevel < 5) {
              text = this.processVariableText(text.replace(fulltemp, newtext), false, nestingLevel + 1);
            } else {
              throw Error('Recursive problem in variable.')
            }
          }
        }
      }

      end += 2; // len of %]
      if (loop.length - (end) <= 0) return text;
      loop = loop.substring(end, loop.length - 1);
    }
  }
}
